import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Boxes,
  AlertTriangle,
  PackageX,
  Search,
  ArrowUpRight,
  Pencil,
  MoreHorizontal,
  Repeat,
  Trash2,
  FlaskConical,
  Percent,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { categoriesService, inventoryService } from '@/services/inventory';
import type {
  InventoryListRow,
  StockStatusFlag,
  InventoryItem,
  ItemCatalogStatus,
  ItemKind,
} from '@/types/inventory';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { InventoryDetailSheet } from './InventoryDetailSheet';
import { ItemFormSheet } from './ItemFormSheet';
import { InventoryInwardSheet } from './InventoryInwardSheet';
import { TransferStockSheet } from './TransferStockSheet';
import { CleanTestDataDialog } from './CleanTestDataDialog';
import { TestBadge } from '@/components/test-data/TestBadge';
import { SyncHsnRatesDialog } from './SyncHsnRatesDialog';
import { HsnRateDriftBanner } from './HsnRateDriftBanner';

type StatusFilter = 'all' | StockStatusFlag;

type CatalogStatusFilter = 'all' | ItemCatalogStatus;

type KindFilter = 'all' | ItemKind;

type SortKey = 'name' | 'onHand' | 'available' | 'price' | 'updated';
type SortState = { key: SortKey; dir: 'asc' | 'desc' };

// Sentinel category-filter values that aren't real category ids.
const CATEGORY_ALL = 'all';
const CATEGORY_NONE = '__uncategorised';

/**
 * The catalog list. With no `kind` prop it is the existing all-items
 * Inventory screen (unchanged). Passing `kind="RAW_MATERIAL"` makes it the
 * filtered "Raw Materials" view — same data, same screen, scoped to raw
 * materials, and new items default to that classification.
 */
export function InventoryPage({ kind }: { kind?: ItemKind } = {}) {
  const qc = useQueryClient();
  const isRawView = kind === 'RAW_MATERIAL';
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  // Catalog lifecycle filter — defaults to 'all' so DISCONTINUED and
  // ARCHIVED items are visible, not just ACTIVE ones.
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatusFilter>('all');
  const [selected, setSelected] = useState<InventoryItem | null>(null);
  // When true, the detail sheet opens straight into Details-tab edit
  // mode — set by the row's "Edit catalog" action.
  const [selectedStartEdit, setSelectedStartEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<InventoryItem | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [inwardOpen, setInwardOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [cleanTestDataOpen, setCleanTestDataOpen] = useState(false);
  const [syncHsnOpen, setSyncHsnOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  // Additional client-side facets (data is ≤200 rows, so filtering/sorting
  // client-side keeps the UX instant without extra round-trips).
  const [categoryFilter, setCategoryFilter] = useState<string>(CATEGORY_ALL);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [sort, setSort] = useState<SortState | null>(null);
  const debounced = useDebounced(search);

  // Test-data tools are gated by the per-tenant `test-data-tools`
  // feature flag — a platform admin enables it for beta tenants only.
  const me = useCurrentUser();
  const testDataEnabled = (me?.featureFlags ?? []).includes('test-data-tools');

  // Both open the same detail sheet — "Edit catalog" just lands on the
  // Details tab already in edit mode.
  const openDetail = (it: InventoryItem) => {
    setSelectedStartEdit(false);
    setSelected(it);
  };
  const openEdit = (it: InventoryItem) => {
    setSelectedStartEdit(true);
    setSelected(it);
  };

  // Catalog items + aggregated stock + categories — three queries combined
  // client-side. Tenant data is small; no aggregation endpoint needed yet.
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { q: debounced, status: catalogStatus, kind: kind ?? 'all' }],
    queryFn: () =>
      inventoryService.list({
        search: debounced || undefined,
        status: catalogStatus === 'all' ? undefined : catalogStatus,
        kind,
        pageSize: 200,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inventoryService.remove(id),
    onSuccess: (res) => {
      toast.success(res.reason);
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Delete failed'),
  });

  const testDataMutation = useMutation({
    mutationFn: ({ id, isTestData }: { id: string; isTestData: boolean }) =>
      inventoryService.setTestDataFlag(id, isTestData),
    onSuccess: (res) => {
      toast.success(res.isTestData ? 'Marked as test data' : 'Unmarked test data');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update'),
  });

  const stockQuery = useQuery({
    queryKey: ['inventory', 'stock'],
    queryFn: () => inventoryService.listStock({ pageSize: 500 }),
    staleTime: 15_000,
  });

  const categoriesQuery = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => categoriesService.list(),
    staleTime: 60_000,
  });

  const items = itemsQuery.data?.items ?? [];
  const stockRows = stockQuery.data?.rows ?? [];
  const categories = categoriesQuery.data ?? [];

  // Aggregate stock per itemId. Phase C — for variant rows, ALSO roll
  // the same numbers up to the parent's bucket so the catalog list
  // (which shows ROOTS) renders aggregate stock on parent rows. Without
  // this, a parent-with-variants always reads "out of stock" even when
  // its variants are well-stocked.
  const stockByItem = useMemo(() => {
    const m = new Map<string, { onHand: number; reserved: number; warehouseCount: number }>();
    const bump = (key: string, onHand: number, reserved: number) => {
      const cur = m.get(key);
      if (cur) {
        cur.onHand += onHand;
        cur.reserved += reserved;
        cur.warehouseCount += 1;
      } else {
        m.set(key, { onHand, reserved, warehouseCount: 1 });
      }
    };
    for (const s of stockRows) {
      const onHand = Number(s.quantityOnHand);
      const reserved = Number(s.quantityReserved);
      bump(s.itemId, onHand, reserved);
      // Roll variant stock up to its parent so the catalog list
      // (ROOTS) reflects aggregate availability without a separate fetch.
      if (s.item.parentItemId) {
        bump(s.item.parentItemId, onHand, reserved);
      }
    }
    return m;
  }, [stockRows]);

  const categoryById = useMemo(() => {
    const m = new Map<string, (typeof categories)[number]>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  // Build aggregated rows.
  const allRows = useMemo<InventoryListRow[]>(() => {
    return items.map((item) => {
      const s = stockByItem.get(item.id);
      const totalOnHand = s?.onHand ?? 0;
      const totalReserved = s?.reserved ?? 0;
      const totalAvailable = Math.max(0, totalOnHand - totalReserved);
      const reorder = item.reorderPoint ?? 0;
      const stockStatus: StockStatusFlag =
        totalOnHand <= 0 ? 'out_of_stock' : reorder > 0 && totalOnHand <= reorder ? 'low' : 'in_stock';
      return {
        item,
        totalOnHand,
        totalAvailable,
        totalReserved,
        warehouseCount: s?.warehouseCount ?? 0,
        stockStatus,
        category: item.categoryId
          ? categoryById.get(item.categoryId)
            ? {
                id: categoryById.get(item.categoryId)!.id,
                code: categoryById.get(item.categoryId)!.code,
                name: categoryById.get(item.categoryId)!.name,
              }
            : undefined
          : undefined,
      };
    });
  }, [items, stockByItem, categoryById]);

  // Category facet options — scoped to the view: the finished-goods Inventory
  // hides INTERNAL (raw-material) categories; the Raw Materials view shows only
  // those. SELLABLE + legacy-undefined default to visible in the finished view.
  const categoryOptions = useMemo(
    () =>
      categories.filter((c) =>
        isRawView ? c.categoryKind === 'INTERNAL' : c.categoryKind !== 'INTERNAL',
      ),
    [categories, isRawView],
  );

  // Apply the category / kind facets (NOT the stock-status tab — that is
  // applied last so the tab counts reflect the currently-selected facets).
  const baseRows = useMemo(() => {
    return allRows.filter((r) => {
      if (categoryFilter === CATEGORY_NONE) {
        if (r.item.categoryId) return false;
      } else if (categoryFilter !== CATEGORY_ALL) {
        if (r.item.categoryId !== categoryFilter) return false;
      }
      if (kindFilter !== 'all' && (r.item.kind ?? 'FINISHED_GOOD') !== kindFilter) return false;
      return true;
    });
  }, [allRows, categoryFilter, kindFilter]);

  const counts = useMemo(() => {
    const c = { all: baseRows.length, in_stock: 0, low: 0, out_of_stock: 0 };
    for (const r of baseRows) c[r.stockStatus] += 1;
    return c;
  }, [baseRows]);

  const filtered = useMemo(() => {
    const byTab =
      filter === 'all' ? baseRows : baseRows.filter((r) => r.stockStatus === filter);
    if (!sort) return byTab;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (r: InventoryListRow): string | number => {
      switch (sort.key) {
        case 'name':
          return r.item.name.toLowerCase();
        case 'onHand':
          return r.totalOnHand;
        case 'available':
          return r.totalAvailable;
        case 'price':
          return r.item.sellingPrice ?? -1;
        case 'updated':
          return r.item.updatedAt;
      }
    };
    return [...byTab].sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [baseRows, filter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' },
    );

  const anyFilterActive =
    !!search ||
    categoryFilter !== CATEGORY_ALL ||
    kindFilter !== 'all' ||
    catalogStatus !== 'all' ||
    filter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter(CATEGORY_ALL);
    setKindFilter('all');
    setCatalogStatus('all');
    setFilter('all');
  };

  const summary = useMemo(() => {
    const onHand = allRows.reduce((s, r) => s + r.totalOnHand, 0);
    return { onHand, low: counts.low, oos: counts.out_of_stock };
  }, [allRows, counts.low, counts.out_of_stock]);

  const tabs: StatusTabOption[] = [
    { value: 'all',          label: 'All',          count: counts.all },
    { value: 'in_stock',     label: 'In stock',     count: counts.in_stock },
    { value: 'low',          label: 'Low',          count: counts.low },
    { value: 'out_of_stock', label: 'Out of stock', count: counts.out_of_stock },
  ];

  const isLoading = itemsQuery.isLoading || stockQuery.isLoading;
  const error = itemsQuery.error ?? stockQuery.error;

  // Clickable header for a client-sorted column, with a direction chevron.
  const sortHeader = (key: SortKey, label: string) => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
    >
      {label}
      {sort?.key === key ? (
        sort.dir === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-30" />
      )}
    </button>
  );

  // Kind is only worth surfacing in the mixed "all" view; the raw-materials
  // view is already scoped, so the badge would be noise there.
  const showKindBadge = !kind;

  const columns: Column<InventoryListRow>[] = [
    {
      key: 'sku',
      header: 'SKU',
      width: '130px',
      cell: (r) => <span className="font-mono text-[11.5px]">{r.item.sku}</span>,
    },
    {
      key: 'name',
      header: sortHeader('name', 'Item'),
      cell: (r) => (
        <div className="flex items-center gap-2.5">
          {r.item.primaryImageUrl ? (
            <img
              src={r.item.primaryImageUrl}
              alt=""
              loading="lazy"
              className="h-8 w-8 shrink-0 rounded-md border border-border/60 object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-dashed border-border/60 bg-muted/40 text-muted-foreground"
            >
              <Boxes className="h-3.5 w-3.5" />
            </span>
          )}
          <div className="min-w-0 space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium text-foreground">{r.item.name}</span>
              {showKindBadge && (r.item.kind ?? 'FINISHED_GOOD') === 'RAW_MATERIAL' && (
                <Badge variant="muted" className="text-[9.5px]">raw</Badge>
              )}
              {r.item.isTestData && <TestBadge />}
              {(r.item.variantCount ?? 0) > 0 && (
                <Badge variant="muted" className="font-mono text-[10px]">
                  +{r.item.variantCount}
                </Badge>
              )}
              {r.item.tracksBatches && (
                <Badge variant="muted" className="font-mono text-[10px]">batched</Badge>
              )}
              {r.item.isBundle && (
                <Badge variant="muted" className="font-mono text-[10px]">bundle</Badge>
              )}
            </div>
            <div className="truncate text-[11px] text-muted-foreground">
              {r.item.unitOfMeasure}
              {r.item.isFeatured && ' · featured'}
              {r.item.isNewArrival && ' · new'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '150px',
      cell: (r) =>
        r.category ? (
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[12px] text-foreground">{r.category.name}</div>
            <div className="font-mono text-[10.5px] text-muted-foreground">{r.category.code}</div>
          </div>
        ) : (
          <span className="text-[12px] text-muted-foreground">Uncategorised</span>
        ),
    },
    {
      key: 'brand',
      header: 'Brand',
      width: '120px',
      cell: (r) =>
        r.item.brand ? (
          <span className="text-[12.5px] text-foreground">{r.item.brand}</span>
        ) : (
          <span className="text-[12px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'tax',
      header: 'HSN / GST',
      width: '104px',
      cell: (r) => {
        const rate = r.item.intraStateTaxRate ?? r.item.interStateTaxRate;
        return (
          <div className="space-y-0.5 leading-tight">
            <div className="font-mono text-[11.5px] text-foreground">{r.item.hsnCode || '—'}</div>
            <div className="text-[11px] text-muted-foreground">
              {rate != null ? `${rate}% GST` : 'no rate'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'onHand',
      header: sortHeader('onHand', 'On hand'),
      align: 'right',
      width: '108px',
      cell: (r) => (
        <div className="leading-tight">
          <span className="font-mono text-[12.5px] tabular-nums">{fmt.number(r.totalOnHand)}</span>
          {r.totalReserved > 0 && (
            <div className="text-[10.5px] text-muted-foreground tabular-nums">
              {fmt.number(r.totalReserved)} reserved
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'available',
      header: sortHeader('available', 'Available'),
      align: 'right',
      width: '100px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums">{fmt.number(r.totalAvailable)}</span>
      ),
    },
    {
      key: 'sellingPrice',
      header: sortHeader('price', 'Price'),
      align: 'right',
      width: '120px',
      cell: (r) => (
        <div className="leading-tight">
          {r.item.sellingPrice != null ? (
            <span className="font-mono text-[12.5px] tabular-nums">{fmt.inr(r.item.sellingPrice)}</span>
          ) : (
            <span className="text-[12px] text-muted-foreground">—</span>
          )}
          {r.item.mrp != null && (
            <div className="text-[10.5px] text-muted-foreground tabular-nums">
              MRP {fmt.inr(r.item.mrp)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      cell: (r) => (
        <div className="flex flex-wrap items-center gap-1">
          <StatusBadge status={r.stockStatus} />
          {r.item.status !== 'ACTIVE' && (
            <Badge variant="muted" className="font-mono text-[10px]">
              {r.item.status.toLowerCase()}
            </Badge>
          )}
          {r.item.isTestData && (
            <Badge
              variant="warning"
              className="gap-1 text-[9.5px]"
              title="Marked as test data — will be removed by Clean test data"
            >
              <FlaskConical className="h-3 w-3" />
              TEST
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '100px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => openDetail(r.item)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => openEdit(r.item)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit catalog</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openDetail(r.item)}>Open detail</DropdownMenuItem>
              <DropdownMenuItem onClick={() => openEdit(r.item)}>Edit catalog</DropdownMenuItem>
              {testDataEnabled && (
                <DropdownMenuItem
                  onClick={() =>
                    testDataMutation.mutate({
                      id: r.item.id,
                      isTestData: !r.item.isTestData,
                    })
                  }
                >
                  <FlaskConical className="h-3.5 w-3.5" />
                  {r.item.isTestData ? 'Unmark test data' : 'Mark as test data'}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setConfirmDelete(r.item)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete item
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title={isRawView ? 'Raw Materials' : 'Inventory'}
          count={counts.all}
          actions={
            <>
              {testDataEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCleanTestDataOpen(true)}
                  className="text-destructive hover:text-destructive"
                >
                  <FlaskConical className="h-3.5 w-3.5" />
                  Clean test data
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setSyncHsnOpen(true)}>
                <Percent className="h-3.5 w-3.5" />
                Sync HSN rates
              </Button>
              <Button variant="outline" size="sm" onClick={() => setTransferOpen(true)}>
                <Repeat className="h-3.5 w-3.5" />
                Transfer
              </Button>
              <Button variant="outline" size="sm" onClick={() => setInwardOpen(true)}>
                <Plus className="h-3.5 w-3.5" />
                Receive stock
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-3.5 w-3.5" />
                New SKU
              </Button>
            </>
          }
        />

        <HsnRateDriftBanner onSync={() => setSyncHsnOpen(true)} />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Units on hand" value={fmt.compact(summary.onHand)} tone="primary" icon={Boxes} />
          <KpiCard label="Low stock" value={fmt.number(summary.low)} tone={summary.low > 0 ? 'warning' : 'default'} icon={AlertTriangle} />
          <KpiCard label="Out of stock" value={fmt.number(summary.oos)} tone={summary.oos > 0 ? 'destructive' : 'default'} icon={PackageX} />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SKU, name, category…"
              className="h-8 w-64 pl-8 text-[13px]"
            />
          </div>

          {/* Type facet — only in the mixed Inventory view (raw view is scoped). */}
          {!kind && (
            <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as KindFilter)}>
              <SelectTrigger className="h-8 w-[150px] text-[12px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="FINISHED_GOOD">Finished goods</SelectItem>
                <SelectItem value="RAW_MATERIAL">Raw materials</SelectItem>
              </SelectContent>
            </Select>
          )}

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-8 w-[180px] text-[12px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CATEGORY_ALL}>All categories</SelectItem>
              <SelectItem value={CATEGORY_NONE}>Uncategorised</SelectItem>
              {categoryOptions.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="font-mono text-[11px] text-muted-foreground">{c.code}</span>
                  <span className="ml-2">{c.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={catalogStatus}
            onValueChange={(v) => setCatalogStatus(v as CatalogStatusFilter)}
          >
            <SelectTrigger className="h-8 w-[140px] text-[12px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="DISCONTINUED">Discontinued</SelectItem>
              <SelectItem value="ARCHIVED">Archived</SelectItem>
            </SelectContent>
          </Select>

          {anyFilterActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}

          <div className="ml-auto flex items-center gap-3">
            <span className="text-[12px] tabular-nums text-muted-foreground">
              {filtered.length === allRows.length
                ? `${allRows.length} item${allRows.length === 1 ? '' : 's'}`
                : `${filtered.length} of ${allRows.length}`}
            </span>
            <DensityToggle value={density} onChange={setDensity} />
          </div>
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => { itemsQuery.refetch(); stockQuery.refetch(); }} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.item.id}
            isLoading={isLoading}
            onRowClick={(r) => openDetail(r.item)}
            density={density}
            emptyTitle={
              allRows.length === 0 ? 'No inventory items yet' : 'No items match your filters'
            }
            emptyDescription={
              allRows.length === 0
                ? 'Create your first SKU to start tracking stock.'
                : 'Adjust or clear the filters to see more items.'
            }
            emptyAction={
              allRows.length === 0 ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  New SKU
                </Button>
              ) : anyFilterActive ? (
                <Button size="sm" variant="outline" onClick={clearFilters}>
                  <X className="h-3.5 w-3.5" />
                  Clear filters
                </Button>
              ) : null
            }
          />
        )}

        <InventoryDetailSheet
          item={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          initialEditCatalog={selectedStartEdit}
        />

        <ItemFormSheet
          item={null}
          defaultKind={kind}
          open={createOpen}
          onOpenChange={setCreateOpen}
        />

        <InventoryInwardSheet open={inwardOpen} onOpenChange={setInwardOpen} />
        <TransferStockSheet open={transferOpen} onOpenChange={setTransferOpen} />
        <SyncHsnRatesDialog open={syncHsnOpen} onOpenChange={setSyncHsnOpen} />

        {testDataEnabled && (
          <CleanTestDataDialog
            open={cleanTestDataOpen}
            onOpenChange={setCleanTestDataOpen}
          />
        )}

        <ConfirmDialog
          open={!!confirmDelete}
          onOpenChange={(o) => !o && setConfirmDelete(null)}
          title={`Delete "${confirmDelete?.name ?? ''}"?`}
          description={
            confirmDelete?.isTestData ? (
              <>
                This item is marked <strong>test data</strong>. Deleting it{' '}
                <strong>permanently removes the item AND every order,
                dispatch, payment and invoice that references it</strong> —
                they're treated as test records. SKU{' '}
                <code className="font-mono">{confirmDelete?.sku}</code> is freed
                for re-use. This cannot be undone.
              </>
            ) : (
              <>
                If this item has no order, dispatch or purchase-order history it
                is <strong>permanently deleted</strong> and its SKU{' '}
                <code className="font-mono">{confirmDelete?.sku}</code> is freed
                for re-use. If it does have history it is <strong>archived</strong>{' '}
                instead — the SKU stays reserved. This cannot be undone.
              </>
            )
          }
          confirmLabel="Delete item"
          tone="destructive"
          onConfirm={async () => {
            if (confirmDelete) await deleteMutation.mutateAsync(confirmDelete.id);
          }}
        />
      </div>
    </TooltipProvider>
  );
}
